import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Link as MuiLink,
  TextField,
  InputAdornment
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Email, ArrowForward, VpnKey } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const EmailVerification: React.FC = () => {
  const [verificationState, setVerificationState] = useState<'verifying' | 'success' | 'error' | 'sending' | 'sent' | 'input'>('verifying');
  type VerificationState = 'verifying' | 'success' | 'error' | 'sending' | 'sent' | 'input';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isEmailVerified, verifyEmail, sendVerificationEmail, refreshUser } = useAuth();

  // Extract token from query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      // If we have a token, verify it
      const verifyToken = async () => {
        try {
          const success = await verifyEmail(token);
          if (success) {
            setVerificationState('success');
          } else {
            setVerificationState('error');
            setErrorMessage('The verification link is invalid or has expired.');
          }
        } catch (err) {
          console.error('Email verification error:', err);
          setVerificationState('error');
          setErrorMessage('Failed to verify your email. Please try again later.');
        }
      };

      verifyToken();
    } else {
      // If no token, show the manual code entry page
      // Check if already verified
      if (isEmailVerified) {
        setVerificationState('success');
      } else {
        setVerificationState('input');
      }
    }
  }, [location.search, verifyEmail, isEmailVerified]);

  // Handle manual code verification
  const handleCodeVerification = async () => {
    if (!verificationCode || verificationCode.trim().length < 6) {
      setErrorMessage('Please enter a valid verification code');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      // Try to verify with the code entered
      const success = await verifyEmail(verificationCode.trim());
      if (success) {
        setVerificationState('success');
        // Refresh user data to update email verification status
        await refreshUser();
      } else {
        setErrorMessage('Invalid verification code. Please check and try again.');
      }
    } catch (err) {
      console.error('Code verification error:', err);
      setErrorMessage('Failed to verify code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle sending verification email
  const handleSendVerification = async () => {
    setVerificationState('sending');
    setErrorMessage(null);

    try {
      const success = await sendVerificationEmail();
      if (success) {
        setVerificationState('sent');
      } else {
        setVerificationState('error');
        setErrorMessage('Failed to send verification email. Please try again later.');
      }
    } catch (err) {
      console.error('Failed to send verification email:', err);
      setVerificationState('error');
      setErrorMessage('An error occurred while sending the verification email.');
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      <Grid container justifyContent="center">
        <Grid xs={12}>
          <Paper
            elevation={3}
            sx={{ p: 4, mt: { xs: 2, sm: 8 }, borderRadius: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {verificationState === 'verifying' && (
                <>
                  <CircularProgress size={60} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Verifying your email...
                  </Typography>
                </>
              )}

              {verificationState === 'success' && (
                <>
                  <CheckCircle color="success" sx={{ fontSize: 60 }} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Email Verified Successfully
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    Your email has been verified. You can now access all features of the application.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    endIcon={<ArrowForward />}
                    component={Link}
                    to="/dashboard"
                  >
                    Go to Dashboard
                  </Button>
                </>
              )}

              {verificationState === 'error' && !isEmailVerified && (
                <>
                  <ErrorIcon color="error" sx={{ fontSize: 60 }} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Email Verification Required
                  </Typography>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                      {errorMessage}
                    </Alert>
                  )}
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    Please verify your email address to access all features of the application.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    startIcon={<Email />}
                    onClick={handleSendVerification}
                    disabled={(verificationState as VerificationState) === 'sending' || (verificationState as VerificationState) === 'sent'}
                  >
                    {(verificationState as VerificationState) === 'sending' ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} /> Sending...
                      </>
                    ) : (verificationState as VerificationState) === 'sent' ? (
                      'Email Sent'
                    ) : (
                      'Send Verification Email'
                    )}
                  </Button>
                </>
              )}

              {verificationState === 'error' && isEmailVerified && (
                <>
                  <ErrorIcon color="error" sx={{ fontSize: 60 }} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Verification Link Expired
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    The verification link has expired, but your email is already verified.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 3 }}
                    endIcon={<ArrowForward />}
                    component={Link}
                    to="/dashboard"
                  >
                    Go to Dashboard
                  </Button>
                </>
              )}

              {verificationState === 'input' && (
                <>
                  <VpnKey color="primary" sx={{ fontSize: 60 }} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Email Verification
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    Enter the verification code from your email or click the link in the email.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Verification Code"
                    variant="outlined"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-8 character code"
                    sx={{ mt: 3 }}
                    disabled={isSubmitting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKey />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: 'center', letterSpacing: '0.2em', fontSize: '1.2rem' },
                      maxLength: 64,
                    }}
                  />

                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                      {errorMessage}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCodeVerification}
                      disabled={isSubmitting || !verificationCode}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircle />}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify Code'}
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSendVerification}
                      startIcon={<Email />}
                    >
                      Resend Email
                    </Button>
                  </Box>

                  <Divider sx={{ width: '100%', mt: 3, mb: 2 }} />

                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                    Didn't receive the email? Check your spam folder or request a new one.
                  </Typography>
                </>
              )}

              {verificationState === 'sent' && (
                <>
                  <Email color="info" sx={{ fontSize: 60 }} />
                  <Typography variant="h5" sx={{ mt: 3 }}>
                    Verification Email Sent
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    We've sent a verification email to your address. Please check your inbox and click the verification link.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    If you don't see the email, check your spam folder or try again in a few minutes.
                  </Typography>
                  <Divider sx={{ width: '100%', my: 3 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                      variant="outlined"
                      onClick={handleSendVerification}
                    >
                      Resend Email
                    </Button>
                    <Button
                      variant="contained"
                      component={Link}
                      to="/dashboard"
                    >
                      Go to Dashboard
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EmailVerification;